import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import { addHours } from 'date-fns';

import Card from 'components/Card';
import Button from 'components/Button';
import Input from 'components/Input';
import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';
import Spinner from 'components/Spinner';
import Select from 'components/Select';

import { ColumnContent, Container, Form, Scroll } from './styles';
import api from 'services/api';
import { Programa } from 'pages/@types';
import { show } from 'store/modules/dialog/actions';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { AxiosResponse } from 'axios';

// configuração do validator
const schema = Yup.object().shape({
  pauta: Yup.string().required('Campo Obrigatório'),
  dataexibicao: Yup.date()
    .required('Campo Obrigatório')
    .typeError('Data inválida'),
  programa: Yup.number()
    .required('Campo Obrigatório')
    .typeError('Programa inválido'),
  titulo: Yup.string().max(80, 'Titulo excedeu o limite de 200 caracteres'),
  resumo: Yup.string().max(500, 'Titulo excedeu o limite de 1000 caracteres'),
});

export default function ORAStore() {
  // react-router-dom
  const navigate = useNavigate();
  // redux
  const dispatch = useDispatch();

  // USE STATE
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingProgramas, setLoadingProgramas] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [programas, setProgramas] = useState<Programa[]>([]);

  // USE PARAMS
  const { oraId } = useParams<{ oraId: string }>();

  // USE REF
  const ref = useRef<FormHandles>(null);

  // USE CALLBACK
  const handleSuccessMessage = useCallback(
    ({ contentHTML, redirectTo }) => {
      const option = {
        type: 'success',
        confirmButtonText: '',
        contentHTML,
        timer: 3000,
        cancelButtonText: 'NÃO',
        redirectTo,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };

      dispatch(show(option));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    async data => {
      try {
        if (ref.current) {
          setLoadingSubmit(true);

          // Realiza validação de campos obrigatórios
          ref.current.setErrors({});
          await schema.validate(data, { abortEarly: false });

          // Envia requisição de criação ou edição para o backend
          let response: AxiosResponse<any, any>;
          if (oraId) response = await api.put(`/ora/${oraId}`, data);
          else response = await api.post('/ora', data);

          // Redireciona usuário em caso de sucesso
          if (response && response.data)
            handleSuccessMessage({
              contentHTML: `<h3>Oportunidade salva!</h3> <p>Você será redirecionado para a lista de oportunidades em 3 segundos</p>`,
              redirectTo: `/ora`,
              type: 'success',
            });
          else toast.error('Falha ao salvar a oportunidade');
        }
      } catch (err) {
        console.log(err);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (ref.current) ref.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      }
      setLoadingSubmit(false);
    },
    [handleSuccessMessage, oraId],
  );

  // USE EFFECT
  useEffect(() => {
    async function loadORA() {
      setLoading(true);
      try {
        const response = await api.get(`/ora/${oraId}`);
        if (response && response.data) {
          const data = response.data;
          setInitialData({
            ...data,
            dataexibicao: addHours(new Date(data.dataexibicao), 3),
            programa: {
              value: data.programa.id,
              label: data.programa.mnemonico,
            },
          });
        } else toast.error('Falha de comunicação com o servidor');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    // Verifica se é edição
    if (oraId) {
      loadORA();
    }
  }, [oraId]);

  useEffect(() => {
    async function loadProgramas() {
      setLoadingProgramas(true);
      try {
        const response = await api.get('/programas');
        if (response && response.data) {
          setProgramas(
            response.data.map((p: Programa) => ({
              value: p.id,
              label: p.mnemonico,
            })),
          );
        } else toast.error('Falha de comunicação com o sistema');
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('Falha ao carregar programas');
          console.log(error);
        }
      }
      setLoadingProgramas(false);
    }
    loadProgramas();
  }, []);

  return (
    <Container>
      <Card>
        <header>
          <h4>CRIAR NOVA OPORTUNIDADE</h4>
        </header>
        {!loading && (
          <Scroll $height="calc(100vh - 160px)">
            <Form
              ref={ref}
              initialData={initialData}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div>
                <section>
                  <h5>Dados gerais: </h5>
                </section>
                <ColumnContent $columns="3fr 2fr 1fr">
                  <Input
                    name="titulo"
                    placeholder="Título"
                    disabled={loadingSubmit || loadingProgramas}
                    max={80}
                  />
                  <Select
                    name="programa"
                    placeholder="Selecione o programa"
                    options={programas}
                    disabled={loadingSubmit || loadingProgramas}
                    isClearable
                  />
                  <DatePicker
                    placeholderText="Data de exibição *"
                    minDate={new Date()}
                    name="dataexibicao"
                    disabled={loadingSubmit || loadingProgramas}
                  />
                </ColumnContent>
              </div>
              <ColumnContent $columns="1fr 1fr">
                <div>
                  <section>
                    <h5>Pauta: </h5>
                  </section>
                  <TextArea
                    name="pauta"
                    placeholder="Pauta"
                    height="calc(100vh - 385px)"
                    disabled={loadingSubmit || loadingProgramas}
                  />
                </div>
                <div>
                  <section>
                    <h5>Resumo: </h5>
                  </section>
                  <TextArea
                    name="resumo"
                    placeholder="Resumo"
                    height="calc(100vh - 385px)"
                    disabled={loadingSubmit || loadingProgramas}
                    max={500}
                  />
                </div>
              </ColumnContent>
              <div>
                <Button
                  color="primary"
                  onClick={e => {
                    e.preventDefault();
                    navigate(-1);
                  }}
                >
                  <FiArrowLeft />
                  VOLTAR
                </Button>
                <div>
                  <Button type="submit" color="primary">
                    <FiSave />
                    {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
                  </Button>
                </div>
              </div>
            </Form>
          </Scroll>
        )}
      </Card>
    </Container>
  );
}
